import { AppleSignIn, ErrorCode as AppleErrorCode, SignInScope } from '@capawesome/capacitor-apple-sign-in';
import { ErrorCode as GoogleErrorCode, GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { getNativePlatform } from '@/utils/capacitor';
import { appleRedirectUri } from '@/utils/appleRedirect';

// @capawesome/capacitor-google-sign-in 0.1.x dan @capawesome/capacitor-apple-sign-in 0.1.x
// peer @capacitor/core >= 8. Bukan Firebase Auth.
// Web Google memakai Google Identity Services lewat @react-oauth/google.
// Web Apple memakai Apple JS popup. Plugin Apple hanya untuk iOS native.

export class SocialLoginCancelled extends Error {
  constructor() {
    super('Login dibatalkan');
    this.name = 'SocialLoginCancelled';
  }
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<{ authorization?: { id_token?: string } }>;
      };
    };
  }
}

const APPLE_JS_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const GOOGLE_USER_CANCELED_MESSAGE = 'The user canceled the sign-in flow.';

function isCanceled(error: unknown, code: string): boolean {
  return typeof error === 'object' && error !== null && 'code' in error && (error as { code?: string }).code === code;
}

export function isGoogleSignInUserCancel(error: unknown): boolean {
  if (!isCanceled(error, GoogleErrorCode.SignInCanceled)) return false;
  const message = error instanceof Error ? error.message : '';
  return message === '' || message === GOOGLE_USER_CANCELED_MESSAGE;
}

let googleInit: Promise<void> | null = null;

export async function requestNativeGoogleIdToken(): Promise<string> {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim();
  if (!clientId) {
    throw new Error('Gagal masuk dengan Google');
  }
  if (!googleInit) {
    googleInit = GoogleSignIn.initialize({ clientId }).catch((error: unknown) => {
      googleInit = null;
      throw error;
    });
  }
  try {
    await googleInit;
    const result = await GoogleSignIn.signIn();
    if (!result.idToken) {
      throw new Error('Gagal masuk dengan Google');
    }
    return result.idToken;
  } catch (error) {
    if (error instanceof SocialLoginCancelled) throw error;
    if (isGoogleSignInUserCancel(error)) {
      throw new SocialLoginCancelled();
    }
    if (error instanceof Error && error.message === 'Gagal masuk dengan Google') throw error;
    throw new Error('Gagal masuk dengan Google');
  }
}

function loadAppleJs(): Promise<void> {
  if (window.AppleID?.auth) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-apple-auth="true"]');
    const onLoad = () => resolve();
    const onError = () => reject(new Error('Gagal masuk dengan Apple'));
    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_JS_SRC;
    script.async = true;
    script.dataset.appleAuth = 'true';
    script.onload = onLoad;
    script.onerror = onError;
    document.head.appendChild(script);
  });
}

async function requestWebAppleIdToken(): Promise<string> {
  const clientId = import.meta.env.VITE_APPLE_CLIENT_ID?.trim();
  if (!clientId) {
    throw new Error('Gagal masuk dengan Apple');
  }
  await loadAppleJs();
  if (!window.AppleID?.auth) {
    throw new Error('Gagal masuk dengan Apple');
  }
  window.AppleID.auth.init({
    clientId,
    scope: 'name email',
    redirectURI: appleRedirectUri(),
    usePopup: true,
  });
  try {
    const response = await window.AppleID.auth.signIn();
    const idToken = response.authorization?.id_token;
    if (!idToken) {
      throw new Error('Gagal masuk dengan Apple');
    }
    return idToken;
  } catch (error) {
    if (
      typeof error === 'object' &&
      error !== null &&
      'error' in error &&
      (error as { error?: string }).error === 'popup_closed_by_user'
    ) {
      throw new SocialLoginCancelled();
    }
    if (error instanceof Error && error.message === 'Gagal masuk dengan Apple') throw error;
    throw new Error('Gagal masuk dengan Apple');
  }
}

async function requestNativeAppleIdToken(): Promise<string> {
  try {
    const result = await AppleSignIn.signIn({
      scopes: [SignInScope.Email, SignInScope.FullName],
    });
    if (!result.idToken) {
      throw new Error('Gagal masuk dengan Apple');
    }
    return result.idToken;
  } catch (error) {
    if (isCanceled(error, AppleErrorCode.SignInCanceled)) {
      throw new SocialLoginCancelled();
    }
    if (error instanceof Error && error.message === 'Gagal masuk dengan Apple') throw error;
    throw new Error('Gagal masuk dengan Apple');
  }
}

export async function requestAppleIdToken(): Promise<string> {
  if (getNativePlatform() === 'ios') {
    return requestNativeAppleIdToken();
  }
  return requestWebAppleIdToken();
}
